import { FC, useContext, useMemo } from 'react';
import Skeleton from '@mui/material/Skeleton';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import {
  getCellViewerDisplayValue,
  getCellViewerFormat,
  getCellViewerSpanId,
} from 'components/ScalarSpreadsheet/utilities/getCellViewerDisplayValue';
import { AsyncCellProps, AsyncCellSkeletonProps } from './AsyncCell.props';

const DEFAULT_LOADING_WIDTH = '3.5rem';

/**
 * Placeholder shown in a cell whose value has not arrived yet. Exported for cells that render
 * a component instead of a value viewer (the Process Management column, for example) so every
 * async cell shows the same affordance.
 */
export const AsyncCellSkeleton: FC<AsyncCellSkeletonProps> = ({ width = DEFAULT_LOADING_WIDTH }) => (
  <span className="value-viewer">
    <Skeleton variant="text" width={width} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
  </span>
);

/**
 * Value viewer for a cell whose value comes from its own request rather than from the payload
 * the sheet was built with.
 *
 * Threading a slow response through the parser changes the SpreadsheetConfig identity, which
 * sends useWorkbook through a full re-evaluation and repaints the entire grid - the flicker
 * users see when a late request lands. Subscribing here confines the update to the one cell:
 * the sheet is built once from the definition data, and each async value fills in where it
 * belongs.
 *
 * Formatting runs through the same helpers DisplayValueViewer uses, so a resolved async cell
 * is indistinguishable from a synchronous one.
 */
const AsyncCell: FC<AsyncCellProps> = ({ cell, isLoading, value, loadingWidth }) => {
  const { format } = useContext(FeaturedSpreadsheetContext);

  // The parser-built cell carries the presentation; the value only exists here. Merging the
  // two keeps number/percent/currency formatting identical to a value the parser had set.
  // An absent value falls back to the cell's own, which is how a cell the parser never filled
  // in renders blank rather than as a coerced zero.
  const resolvedCell = useMemo(() => ({ ...cell, value: value ?? cell.value }), [cell, value]);
  const cellFormat = useMemo(() => getCellViewerFormat(resolvedCell, format), [resolvedCell, format]);
  const displayValue = useMemo(
    () => getCellViewerDisplayValue(resolvedCell, cellFormat),
    [cellFormat, resolvedCell]
  );
  const spanId = useMemo(() => getCellViewerSpanId(cell), [cell]);

  if (isLoading) {
    return <AsyncCellSkeleton width={loadingWidth} />;
  }

  return (
    <span className="value-viewer">
      <span id={spanId} className={`text-cell ${cell.className ?? ''}`}>
        {displayValue}
      </span>
    </span>
  );
};

export default AsyncCell;
